import { useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet";
import { useDispatch, useSelector } from "react-redux";
import ModalCancel from "../../../components/modalCancel";
import { changeDynamicBooking } from "../../../redux/actions/options";
import Localization from "../../../localization";
import Button from "../../../components/button";
import IconClose from "../../../components/icons/close";
import Progress from "../../../components/progress";
import IconArrow from "../../../components/icons/arrow";
import Step from "./Step";
import { addOpenCallDynamicApplications } from "../../../redux/actions/openCalls";

const Booking = () => {
    const selectedType = useSelector(
        (state) => state.options.isDynamicBookingVisible
    );
    const locale = useSelector((state) => state.app.locale);
    const openCallsData = useSelector((state) => state.openCalls.data);
    const isAdding = useSelector((state) => state.openCalls.isAdding);
    const error = useSelector((state) => state.openCalls.errorMessage);

    const [isVisible, setIsVisible] = useState(false);
    const [cancelModalVisible, setCancelModalVisible] = useState(false);
    const [successModalVisible, setSuccessModalVisible] = useState(false);
    const [currentStep, setCurrentStep] = useState(1);
    const [ready, setReady] = useState(false);
    const [errorMessage, setErrorMessage] = useState(null);
    const [finished, setFinished] = useState(false);
    const [stepValues, setStepValues] = useState({});
    const sent = useRef(false);

    const formikRef = useRef();
    const dispatch = useDispatch();

    useEffect(() => {
        setIsVisible(!!selectedType);
    }, [selectedType]);

    useEffect(() => {
        if (ready) {
            const newValues = Object.keys(stepValues).map((stepValueKey) =>
                Object.values(stepValues[stepValueKey])
            );
            dispatch(addOpenCallDynamicApplications(openCall.id, newValues));
        }
    }, [ready]);

    useEffect(() => {
        if (isAdding && !sent.current) {
            sent.current = true;
        }
        if (!isAdding && sent.current) {
            sent.current = false;
            if (error) {
                let message = Localization[locale].artistic_booking.error;

                if (error.data && error.data.errors) {
                    Object.values(error.data.errors).forEach((item) =>
                        item.forEach((error) => {
                            message += "\n- " + error;
                        })
                    );
                }

                setErrorMessage(message);
            } else {
                setErrorMessage(null);
                setFinished(true);
            }
        }
    }, [isAdding, error]);

    // Hide if not selected
    if (!selectedType) return null;

    const openCall = openCallsData.find((item) => item.type === selectedType);

    // Hide if open call doesn't exist
    if (!openCall) return null;

    const totalSteps = openCall.form.pages.length;

    // Close Modal
    const _onPress = () => {
        setIsVisible(false);
        setTimeout(() => {
            dispatch(changeDynamicBooking(false));
        }, 300);
    };

    const _showCancelModal = () => {
        if (finished) {
            _onPress();
            return;
        }

        setCancelModalVisible(true);
    };

    const _onSubmit = (values) => {
        setStepValues((old) => ({ ...old, [currentStep]: values }));

        if (currentStep === totalSteps) {
            setReady(true);
        } else {
            setCurrentStep((old) => old + 1);
        }
    };

    const _validationForm = async () => {
        if (ready) return;

        if (formikRef.current) {
            formikRef.current.submitForm();
        }
    };

    const _renderOverlay = () => {
        return (
            <div
                className={`z-20 fixed inset-0 bg-black transition duration-500 ${
                    isVisible
                        ? "opacity-60 pointer-events-auto"
                        : "opacity-0 pointer-events-none"
                }`}
                onClick={_showCancelModal}
            >
                <div className="absolute inset-0 bg-gray-500 opacity-75" />
            </div>
        );
    };

    const _renderHeader = () => {
        return (
            <div className="flex mb-60">
                <p className="text-30 mr-30 font-judge font-bold">
                    {Localization[locale][selectedType + "_booking"].title}
                </p>
                <div className="flex-1" />
                <div className="w-50 xl:w-70">
                    <Button
                        icon={
                            <IconClose className="w-25 h-25 z-10 transition hover-hover:group-hover:text-white text-blue" />
                        }
                        onClick={_showCancelModal}
                        style={"w-50 xl:w-70"}
                    />
                </div>
            </div>
        );
    };

    const _renderStep = () => {
        if (finished) {
            return (
                <div className="flex-1 relative">
                    <p className="font-bold text-20 xl:text-30 text-blue mb-40">
                        {Localization[locale]["artistic_booking"].success}
                    </p>
                    <div className="flex">
                        <Button
                            text={
                                Localization[locale]["artistic_booking"]
                                    .success_button
                            }
                            textStyle={
                                "font-bold text-17 z-10 transition hover-hover:group-hover:text-white text-blue"
                            }
                            backgroundColor="bg-grey"
                            onClick={_showCancelModal}
                            wrapperStyle="px-20"
                        />
                    </div>
                </div>
            );
        }

        const page = openCall.form.pages[currentStep - 1];

        return (
            <Step
                key={currentStep}
                stepValues={stepValues[currentStep]}
                page={page}
                innerRef={formikRef}
                onSubmit={_onSubmit}
            />
        );
    };

    const _renderProgress = () => {
        const percentage = Math.round(((currentStep - 1) / totalSteps) * 100);

        if (finished) return null;

        return (
            <div className="flex h-46 items-center mt-20">
                <Progress locale={locale} percentage={percentage} />
                <div className="flex">
                    {currentStep > 1 && (
                        <Button
                            icon={
                                <IconArrow
                                    className={
                                        "w-26 h-26 z-10 transition hover-hover:group-hover:text-white text-blue transform -rotate-90"
                                    }
                                />
                            }
                            backgroundColor="bg-grey"
                            onClick={() => {
                                if (ready) return;
                                setCurrentStep((old) => old - 1);
                            }}
                            style="mr-10"
                            wrapperStyle="px-20"
                        />
                    )}
                    {currentStep < totalSteps && (
                        <Button
                            icon={
                                <IconArrow
                                    className={
                                        "w-26 h-26 z-10 transition hover-hover:group-hover:text-white text-blue transform rotate-90"
                                    }
                                />
                            }
                            backgroundColor="bg-grey"
                            onClick={() => {
                                _validationForm();
                            }}
                            wrapperStyle="px-20"
                        />
                    )}
                    {currentStep === totalSteps && (
                        <Button
                            text={Localization[locale].artistic_booking.submit}
                            textStyle={
                                "font-bold text-17 z-10 transition hover-hover:group-hover:text-white text-blue"
                            }
                            backgroundColor="bg-grey"
                            onClick={() => {
                                setSuccessModalVisible(true);
                            }}
                            wrapperStyle="px-20"
                            isLoading={isAdding}
                        />
                    )}
                </div>
            </div>
        );
    };

    return (
        <div>
            <Helmet>
                <body className="modal-open" />
            </Helmet>
            {_renderOverlay()}
            <div
                className={`fixed bottom-0 top-0 left-0 flex justify-end right-0 transition duration-700 ease-in-out transform z-20 ${
                    isVisible ? "" : "translate-x-full"
                }`}
            >
                <div onClick={_showCancelModal} className="xl:w-1/2" />
                <div className="flex flex-col w-full xl:w-1/2 px-16 xl:px-50 pt-30 xl:pt-40 pb-30 xl:pb-40 bg-grey">
                    {_renderHeader()}
                    <div className="flex-1 overflow-y-auto">
                        {_renderStep()}
                    </div>
                    {errorMessage && (
                        <p className="text-12 text-red mt-6 whitespace-pre-wrap">
                            {errorMessage}
                        </p>
                    )}
                    {_renderProgress()}
                </div>
            </div>
            <ModalCancel
                visible={cancelModalVisible}
                data={true}
                title={Localization[locale]["artistic_booking"].cancel_title}
                text={Localization[locale]["artistic_booking"].cancel_text}
                onClose={(cancel) => {
                    if (cancel) _onPress();

                    setCancelModalVisible(false);
                }}
            />
            <ModalCancel
                visible={successModalVisible}
                data={true}
                title={Localization[locale]["artistic_booking"].confirm_title}
                text={Localization[locale]["artistic_booking"].confirm_text}
                onClose={(success) => {
                    if (success) _validationForm();

                    setSuccessModalVisible(false);
                }}
            />
        </div>
    );
};

export default Booking;
